import * as React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ActiveRallyVideo, AiTurn } from "./ActiveRallyVideo";
import { RallyColumnsStrip } from "./RallyColumnsStrip";
import { CondensedRallyTimeline } from "./CondensedRallyTimeline";
import { RallyFactsChips } from "./RallyFactsChips";
import { AskAiPanel } from "./RallyColumnAskAIPanel";
import { SideoutGameInfluenceReport } from "./RallyPopup";
import {
  computeFirefightsForAllRows,
  FirefightClip,
} from "./GameStory_firefights";
import {
  FocusedReasonStakeForTeam,
  PlayerIndexMap,
  RallyBadge,
  teamOfPlayerId,
} from "./GameStoryDialog_UPDATED";
import { InsightsJson } from "../../shared/types/insightsEtcTypes";
import {
  buildMatchImpactFactsFromInsights,
  MatchImpactFacts,
} from "./GameStory_buildMatchImpactFactsFromInsights";
import {
  FIREFIGHT_MIN_SHOTS,
  FIREFIGHT_REACTION_THRESHOLD_MS,
} from "./GameStory_thresholds";

import { STORY_FILTER_ICON, teamRgba } from "./GameStoryDialog_UPDATED";
import { CvJson } from "../../shared/types/cvJson";
import {PounceWindowRow} from "../../shared/types/pounceEtc";

type PlayerId = number; // PBV player_id, usually 0..3
type FaultReportRow = {
  playerId: PlayerId | null; // null => unknown
  ralliesEndedWithFault: number;
  nextToLastForcedFault: number;
  popupPotentialShots: number;
  popupCausedShots: number;
  // NEW:
  missedThirdDrop: number;
  missedThirdDrive: number;
};

function isMissLike(label: string): boolean {
  const t = label.toLowerCase();
  return (
    t.includes("fault_") ||
    t.includes("unforced_error") ||
    t.includes("into_net") ||
    t.includes("miss")
  );
}

function isThirdShot(shotNumber1Based: number): boolean {
  // shotNumber1Based: 1=serve,2=return,3=third
  return shotNumber1Based === 3;
}

function isDropLike(label: string): boolean {
  const t = label.toLowerCase();
  return t.includes("drop");
}

function isDriveLike(label: string): boolean {
  const t = label.toLowerCase();
  return t.includes("drive");
}

function isFaultShotLabel(s: string): boolean {
  const t = s.toLowerCase();
  // match your existing tokens shown in the shot list
  return (
    t.includes("fault_net") ||
    t.includes("fault_out") ||
    t.includes("fault_short") ||
    // sometimes faults appear as "forced_fault" but that’s not a fault by hitter; keep separate
    false
  );
}

function hasForcedFaultMarker(s: string): boolean {
  const t = s.toLowerCase();
  return t.includes("forced_fault") || t.includes("winner_type:forced_fault");
}

function hasPopupPotential(s: string): boolean {
  return s.toLowerCase().includes("popup:potential");
}

function hasPopupCaused(s: string): boolean {
  return s.toLowerCase().includes("popup_caused");
}

/**
 * shotsByRallyIndex: map rallyIndex -> shot array
 * Each shot needs:
 *  - playerId (number|null)
 *  - label text (the tokenized string you already show: e.g. "fault_net, net" or "winner_type:forced_fault")
 */
export function buildFaultReport(args: {
  shotsByRallyIndex: Record<
    number,
    { playerId: number | null; label: string; shotType: string | null }[]
  >;
}): FaultReportRow[] {
  const { shotsByRallyIndex } = args;

  const byPlayer = new Map<string, FaultReportRow>();
  const keyOf = (pid: number | null) => (pid == null ? "unknown" : String(pid));

  function getRow(pid: number | null): FaultReportRow {
    const k = keyOf(pid);
    const existing = byPlayer.get(k);
    if (existing) return existing;
    const created: FaultReportRow = {
      playerId: pid,
      ralliesEndedWithFault: 0,
      nextToLastForcedFault: 0,
      popupPotentialShots: 0,
      popupCausedShots: 0,
      missedThirdDrop: 0,
      missedThirdDrive: 0,
    };
    byPlayer.set(k, created);
    return created;
  }

  for (const rallyIndexStr of Object.keys(shotsByRallyIndex)) {
    const shots = shotsByRallyIndex[Number(rallyIndexStr)] ?? [];
    if (!shots.length) continue;

    // (A) rally ended with a fault (last shot is a fault)
    const last = shots[shots.length - 1];
    if (last && isFaultShotLabel(last.label)) {
      getRow(last.playerId).ralliesEndedWithFault += 1;
    }

    // (B) next-to-last had forced fault marker
    if (shots.length >= 2) {
      const prev = shots[shots.length - 2];
      if (prev && hasForcedFaultMarker(prev.label)) {
        getRow(prev.playerId).nextToLastForcedFault += 1;
      }
    }

    // (C) popup_potential shots anywhere
    for (const sh of shots) {
      if (hasPopupPotential(sh.label)) {
        getRow(sh.playerId).popupPotentialShots += 1;
      }

      if (hasPopupCaused(sh.label)) {
        getRow(sh.playerId).popupCausedShots += 1;
      }

      // (D) missed 3rd-shot drop/drive (serve=0, return=1, third=2)
      if (shots.length >= 3) {
        const third = shots[2];
        if (third && isMissLike(third.label)) {
          const row = getRow(third.playerId);
          const st =
            typeof third.shotType === "string"
              ? third.shotType.toLowerCase()
              : null;
          if (st === "drop") row.missedThirdDrop += 1;
          if (st === "drive") row.missedThirdDrive += 1;
        }
      }

      // if (shots.length >= 3) {
      //   const third = shots[2]; // 0-based
      //   if (third && isMissLike(third.label)) {
      //     const row = getRow(third.playerId);
      //     if (isDropLike(third.label)) row.missedThirdDrop += 1;
      //     if (isDriveLike(third.label)) row.missedThirdDrive += 1;
      //   }
      // }
    }
  }

  // stable ordering: 0..3 then unknown
  const rows = Array.from(byPlayer.values());
  rows.sort((a, b) => {
    const av = a.playerId == null ? 999 : a.playerId;
    const bv = b.playerId == null ? 999 : b.playerId;
    return av - bv;
  });
  return rows;
}

// type AiTurn = {
//   id: string;
//   createdAtIso?: string | null;
//   question?: string | null;
//   answerMd?: string | null;
//   prompt?: string | null;
//   responseMd?: string | null;
// };

export function rallyKey(r: {
  rallyIndex: number;
  segmentStartMs: number;
  segmentEndMs: number;
}) {
  // match RallyPopup semantics: unique per (rally, segment)
  return `r${r.rallyIndex}__${Math.round(r.segmentStartMs)}__${Math.round(r.segmentEndMs)}`;
}

async function postJson<T>(url: string, body: any): Promise<T> {
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`POST ${url} failed (${r.status}): ${txt}`);
  }
  return (await r.json()) as T;
}

// Copy these types from RallyPopup.tsx (or import if you prefer).
export type Team01 = 0 | 1;

// export type ShotOverlayUsed =
//   | "actions.shot"
//   | "actions.ball"
//   | "actions.ball_projection"
//   | "hitter_point";

// export type ShotNumberOverlay = {
//   shotNumber: number; // 1-based
//   playerId: number | null;
//   shotType: string | null;

//   atMsRel: number; // relative to segmentStartMs
//   u: number; // 0..1
//   v: number; // 0..1

//   showFromMsRel: number;
//   fullFromMsRel: number;
//   fadeStartMsRel: number;
//   hideAtMsRel: number;

//   frameIdx?: number;
//   used: ShotOverlayUsed;
//   usedConfidence: number | null;

//   featuresLabel?: string | null;
//   label: string | null;
//   followUv:
//     | Array<{
//         dtMs: number;
//         u: number;
//         v: number;
//         used: "actions.ball" | "actions.ball_projection";
//         confidence: number | null;
//       }>
//     | undefined;
// };

export type Vec2 = { x: number; y: number };
export type Vec3 = { x: number; y: number; z: number };

export type PbvTrajectoryPoint = {
  ms: number;
  location: Vec3;
  zone: string;
};

export type PbvTrajectory = {
  confidence?: number;
  start?: PbvTrajectoryPoint;
  peak?: Vec3;
  end?: PbvTrajectoryPoint;
};

export type PbvBallMovementAngles = {
  yaw?: number;
  pitch?: number;
  direction?: string;
};

export type PbvResultingBallMovement = {
  angles?: PbvBallMovementAngles;
  height_over_net?: number;
  speed?: number;
  distance?: number;
  crossed_net?: boolean;
  is_volleyed?: boolean;
  distance_from_baseline?: number;
  trajectory?: PbvTrajectory;
};

export type PbvShot = {
  player_id?: number;

  start_ms?: number;
  end_ms?: number;

  // optional fields you might use later (present in your sample)
  is_final?: boolean;
  quality?: { overall?: number; execution?: number };
  is_volley?: boolean;
  stroke_side?: string;
  stroke_type?: string;
  vertical_type?: string;
  is_passing?: boolean;
  player_positions?: Vec2[];
  advantage_scale?: number[];
  shooter_movement_from_last_shot?: Vec2;
  tags?: Record<string, unknown>;

  // fields you currently read
  shot_type?: string;
  is_speedup?: boolean;
  is_putaway?: boolean;
  winner_type?: string;

  errors?: {
    popup?: unknown;
    unforced?: boolean;
    faults?: {
      net?: boolean;
      kitchen?: boolean;
      short?: boolean;
      out?: { direction?: string } | boolean;
    };
  };

  resulting_ball_movement?: PbvResultingBallMovement;
};

export type PbvRally = {
  start_ms?: number;
  end_ms?: number;
  duration_ms?: number;
  shots?: PbvShot[];
};

// CV types (keep light)
export type CvFrame = { t_ms?: number; [k: string]: unknown };
export type CvRally = { frames?: CvFrame[] };
export type CvRoot = {
  camera?: { fps?: number };
  sessions?: Array<{ rallies?: CvRally[] }>;
  rallies?: CvRally[];
};

export type ShotOverlayUsed =
  | "actions.shot"
  | "actions.ball"
  | "actions.ball_projection"
  | "hitter_point";

export type ShotNumberOverlay = {
  focusedBadge?: FocusedReasonStakeForTeam; // NEW: only set on the matching shot
  rallyIndex: number;

  rawShot: PbvShot | null; // for potential story logic; frontend can ignore if too bulky

  pointKey: string | undefined;
  videoUrl: string | undefined;

  shotKey: string;
  shotNumber: number; // 1-based within rally
  playerId: number | null;
  // NEW: PBV shot_type from insights (e.g. "drop" | "drive" | "dink" | ...).
  // Keep as string for now to avoid schema churn.
  shotType: string | null;

  // --- Story helpers (all optional-ish; derived from insights shot) ---
  endZone?: string | null; // resulting_ball_movement.trajectory.end.zone
  isSpeedup?: boolean | null;
  isPutaway?: boolean | null;
  winnerType?: string | null; // "clean" | "forced_fault" | ...
  popupExploited?: boolean | null;
  faultType?: string | null; // "net" | "out:long" | "out:wide" | "kitchen" | ...

  atMsRel: number; // relative to *segmentStartMs*
  u: number; // normalized 0..1
  v: number; // normalized 0..1

  player_positions?: Vec2[] | undefined;
  advantage_scale?: number[] | undefined;
  shooter_movement_from_last_shot: Vec2 | undefined;

  // NEW: court/world-space ball trajectory locations
  trajectoryConfidence: number | null;
  trajectoryStart: {
    ms: number | null;
    zone: string | null;
    location: Vec3 | null;
  } | null;
  trajectoryEnd: {
    ms: number | null;
    zone: string | null;
    location: Vec3 | null;
  } | null;
  trajectoryPeak: Vec3 | null;

  showFromMsRel: number;
  fullFromMsRel: number;
  fadeStartMsRel: number;
  hideAtMsRel: number;

  // optional debug
  frameIdx?: number;
  used: ShotOverlayUsed;
  usedConfidence?: number | null;
  label: string | null;
  featuresLabel?: string | null;

  followUv:
    | Array<{
        dtMs: number;
        u: number;
        v: number;
        used: "actions.ball" | "actions.ball_projection";
        confidence: number | null;
      }>
    | undefined;

  // ✅ story plumbing (frontend will use later)
  storyKinds?: string[] | undefined; // e.g. ["popup_exploited", "kitchen_arrival_first"]
  story?: Record<string, unknown> | undefined; // optional structured payload (small)
};

export type KitchenArrivalRow = {
  rallyIndex: number;
  pointKey: string | number | null;
  shotType: string | null;
  likely_correct_score?: boolean | null;

  startMs: number;
  endMs: number;
  numShots?: number;

  winnerTeam: 0 | 1 | null;
  winnerSide: "near" | "far" | "unknown";

  servingTeam: 0 | 1 | null;
  servingSide: "near" | "far" | "unknown";
  serverNumber?: number | null;
  serveSpeedMPH: number | null;

  pointAwardedTeam: Team01 | null;
  pointAwardedToServer: boolean | null;

  scoreBefore: [number, number];
  scoreAfter: [number, number];

  scoredPoint: boolean; // servingTeam won the rally (trusted PBV winnerTeam) => point for server team
  serveOutcome: "POINT" | "SERVER_LOST";

  servingArrivalSuccess: boolean | null;
  servingEarliestArrivalMsFromStart: number | null;
  servingHadOpportunity: boolean | null;

  segmentStartMs: number;
  segmentEndMs: number;
  segmentReason: string;

  videoUrl: string;
  shotNumberOverlays?: ShotNumberOverlay[];

  // First-pass experiment: shot where the rally appears to tilt toward the winner.
  breakShotNumber?: number;
  breakShotNumbers?: number[];
};

export type ReportResponse = {
  rows: KitchenArrivalRow[];
  turnsByKey?: Record<string, any[]>;
  sideOutGameInfluenceReport?: SideoutGameInfluenceReport;
  insightsJson: InsightsJson;
  cvJson?: CvJson | null;
  playerIdToAvatarBase64: Record<number, string>;
  pounceWindowRows: PounceWindowRow[]
};

function scrollRallyIntoView(
  m: React.MutableRefObject<Map<number, HTMLElement>>,
  rallyIndex: number,
) {
  const el = m.current.get(rallyIndex);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
}

export function RallyBoardPopup(props: {
  insightsToCvPlayerIndex:PlayerIndexMap
  open: boolean;
  onClose: () => void;
  packageId: string;
  rallyBadgesRef: React.MutableRefObject<Map<number, RallyBadge[]>>;
}) {
  const { open, onClose, packageId, rallyBadgesRef, insightsToCvPlayerIndex } = props;

  const [matchImpactFacts, setMatchImpactFacts] =
    React.useState<MatchImpactFacts | null>(null);
  const [iInsightsJson, setInsightsJson] = React.useState<InsightsJson | null>(
    null,
  );

  const [rows, setRows] = React.useState<KitchenArrivalRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [activeRallyIndex, setActiveRallyIndex] = React.useState<number | null>(
    null,
  );
  const [activeShotNumber, setActiveShotNumber] = React.useState<number | null>(
    null,
  );

  const [firefightClips, setFirefightClips] = React.useState<FirefightClip[]>(
    [],
  );

  const condensedRefs = React.useRef(new Map<number, HTMLElement>());
  const detailedRefs = React.useRef(new Map<number, HTMLElement>());

  const [turnsByKey, setTurnsByKey] = React.useState<Record<string, AiTurn[]>>(
    {},
  );

  const setTurnsForKey = React.useCallback((key: string, turns: AiTurn[]) => {
    setTurnsByKey((cur) => ({ ...cur, [key]: turns }));
  }, []);

  const [localOpen, setLocalOpen] = React.useState(open);

  React.useEffect(() => {
    setLocalOpen(open);
  }, [open]);

  const registerCondensedRef = React.useCallback(
    (rallyIndex: number, el: HTMLDivElement | null) => {
      if (!el) return;
      condensedRefs.current.set(rallyIndex, el);
    },
    [],
  );

  const registerDetailedRef = React.useCallback(
    (rallyIndex: number, el: HTMLDivElement | null) => {
      if (!el) return;
      detailedRefs.current.set(rallyIndex, el);
    },
    [],
  );

  const onPickRallyFromCondensed = React.useCallback((rallyIndex: number) => {
    setActiveRallyIndex(rallyIndex);
    setActiveShotNumber(null);

    // wait a tick so React renders selection styles, then scroll detailed
    requestAnimationFrame(() => scrollRallyIntoView(detailedRefs, rallyIndex));
  }, []);

  const onPickRallyFromDetailed = React.useCallback((rallyIndex: number) => {
    setActiveRallyIndex(rallyIndex);
    setActiveShotNumber(null);

    requestAnimationFrame(() => scrollRallyIntoView(condensedRefs, rallyIndex));
  }, []);

  React.useEffect(() => {
    if (iInsightsJson) {
      const result = buildMatchImpactFactsFromInsights({
        packageId: packageId,
        insights: iInsightsJson,
      });
// console.error("buildMatchImpactFactsFromInsights", { result });
      setMatchImpactFacts(result);
    }
  }, [iInsightsJson]);

  React.useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fetch(`/api/kitchen-arrival-report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ packageId }),
        });
        if (cancelled) return;

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Report load failed: ${res.status} ${txt}`);
        }

        const data = (await res.json()) as ReportResponse;
        const nextRows = (data.rows ?? []).slice().sort((a, b) => {
          if (a.rallyIndex !== b.rallyIndex) return a.rallyIndex - b.rallyIndex;
          return a.segmentStartMs - b.segmentStartMs;
        });

        setRows(nextRows);

        setInsightsJson(data.insightsJson ?? {});

// console.error({ nextRows });

        // Choose a default active rally
        if (nextRows.length && activeRallyIndex == null) {
          setActiveRallyIndex(nextRows[0]!.rallyIndex);
        }
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packageId]);

  const activeRow = React.useMemo(() => {
    if (activeRallyIndex == null) return null;
    return rows.find((r) => r.rallyIndex === activeRallyIndex) ?? null;
  }, [rows, activeRallyIndex]);

  const onPickRally = React.useCallback((rallyIndex: number) => {
    setActiveRallyIndex(rallyIndex);
    setActiveShotNumber(null);
  }, []);

  React.useEffect(() => {
    let firefightClips: FirefightClip[] = computeFirefightsForAllRows({
      rows: rows,
      thresholdMs: FIREFIGHT_REACTION_THRESHOLD_MS,
      fireFightMinShots: FIREFIGHT_MIN_SHOTS,
      getTeamFromPlayerId: (playerId: number) =>
        teamOfPlayerId(playerId) as 0 | 1, // your helper function
      preMs: 700,
      postMs: 700,
    });

    setFirefightClips(firefightClips);
  }, [rows]);

  const firefightRallySet = React.useMemo(() => {
    const s = new Set<number>();
    for (const c of firefightClips ?? []) {
      if (typeof c?.seq?.rallyIndex === "number") s.add(c.seq.rallyIndex);
    }

    return s;
  }, [firefightClips]);

  //   const onPickShot = React.useCallback((args: {
  //     rallyIndex: number;
  //     shotNumber: number;
  //   }) => {
  //     setActiveRallyIndex(args.rallyIndex);
  //     setActiveShotNumber(args.shotNumber);
  //   }, []);

  const onPickShot = React.useCallback(
    (args: { rallyIndex: number; shotNumber: number }) => {
      setActiveRallyIndex(args.rallyIndex);
      setActiveShotNumber(args.shotNumber);

      requestAnimationFrame(() => {
        scrollRallyIntoView(condensedRefs, args.rallyIndex);
        scrollRallyIntoView(detailedRefs, args.rallyIndex);
      });
    },
    [],
  );

  const handleAskAi = React.useCallback(
    async (args: { row: KitchenArrivalRow; question: string }) => {
      const url = `/api/ai-ask-rally`;

      const body = {
        packageId,
        rallyIndex: args.row.rallyIndex,
        question: args.question,
        segmentStartMs: args.row.segmentStartMs,
        segmentEndMs: args.row.segmentEndMs,
        locator: null, // keep simple in RallyBoard for now
      };

      const resp = await postJson<any>(url, body);

      const key = rallyKey(args.row);

      const nextTurns: AiTurn[] = Array.isArray(resp?.turns)
        ? resp.turns
        : resp?.turn
          ? [...(turnsByKey[key] ?? []), resp.turn]
          : (turnsByKey[key] ?? []);

      setTurnsForKey(key, nextTurns);
    },
    [packageId, turnsByKey, setTurnsForKey],
  );

  const handleDeleteTurn = React.useCallback(
    async (row: KitchenArrivalRow, turnId: string) => {
      const url = `/api/ai-delete-rally-turn`;

      await postJson(url, {
        packageId,
        rallyIndex: row.rallyIndex,
        turnId,
      });

      const key = rallyKey(row);
      setTurnsForKey(
        key,
        (turnsByKey[key] ?? []).filter((t) => t.id !== turnId),
      );
    },
    [packageId, turnsByKey, setTurnsForKey],
  );

  const handleDialogClose = React.useCallback(
    (_event: any, _reason?: "backdropClick" | "escapeKeyDown") => {
      setLocalOpen(false);
      onClose();
    },
    [onClose],
  );

  const shotsByRallyIndex = React.useMemo(() => {
    const out: Record<
      number,
      { playerId: number | null; label: string; shotType: string | null }[]
    > = {};

    for (const r of rows) {
      // wherever you currently compute the rows for the bottom “shot list”
      // I’m assuming you have something like r.shots already.
      out[r.rallyIndex] = (r.shotNumberOverlays ?? []).map(
        (s: ShotNumberOverlay) => ({
          playerId: typeof s.playerId === "number" ? s.playerId : null,
          label: String(s.label ?? ""),
          shotType: s.shotType,
        }),
      );
    }

    return out;
  }, [rows]);

  const faultReportRows = React.useMemo(() => {
    return buildFaultReport({ shotsByRallyIndex });
  }, [shotsByRallyIndex]);

  return (
    <Dialog open={localOpen} onClose={handleDialogClose} maxWidth={false}>
      <DialogTitle sx={{ pr: 6 }}>
        Rally Board
        <IconButton
          onClick={() => {
            setLocalOpen(false);
            onClose();
          }}
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          width: "92vw",
          height: "78vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {loading && (
          <Typography sx={{ opacity: 0.8, mb: 1 }}>Loading…</Typography>
        )}
        {loadError && (
          <Typography sx={{ color: "error.main", mb: 1 }}>
            {loadError}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              flex: "0 0 auto",
              display: "flex",
              gap: 1.2,
              alignItems: "stretch",
              minHeight: 0,
            }}
          >
            {/* LEFT: rally facts */}
            <Box sx={{ flex: "1 1 0", minWidth: 320, maxWidth: 520 }}>
              <Box
                sx={{
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 2,
                  p: 1.2,
                  height: "100%",
                }}
              >
                <RallyFactsChips row={activeRow} />

                <Box sx={{ mt: 1.5, fontSize: 11 }}>
                  <Box sx={{ fontWeight: 700, mb: 0.5 }}>Summary</Box>

                  {faultReportRows.length === 0 ? (
                    <Box sx={{ opacity: 0.7 }}>No shots found.</Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {faultReportRows.map((row) => {
                        const label =
                          row.playerId == null
                            ? "Unknown"
                            : `Player ${row.playerId}`;
                        return (
                          <Box
                            key={
                              row.playerId == null
                                ? "unknown"
                                : String(row.playerId)
                            }
                            sx={{ lineHeight: 1.25 }}
                          >
                            <b>{label}:</b> End-fault{" "}
                            {row.ralliesEndedWithFault}
                            {" · "}3rd miss Dp {row.missedThirdDrop}
                            {" · "}3rd miss Dr {row.missedThirdDrive}
                            {" · "}FF(prev) {row.nextToLastForcedFault}
                            {" · "}PopPot {row.popupPotentialShots}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* MIDDLE: bounded video */}
            <Box
              sx={{
                flex: "0 0 auto",
                width: 640,
                maxWidth: "44vw",
                maxHeight: 360,
              }}
            >
              <ActiveRallyVideo
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                aiContextLabel="Rally Board"
                focusedFilter={null}
                controlsPlacement="bottom"
                packageId={packageId}
                row={activeRow}
                activeShotNumber={activeShotNumber}
                rows={rows}
                showCondensedTimeline={true}
                fontColor={"red"}
                firefightsClips={firefightClips}
                firefightRallySet={firefightRallySet}
              />
            </Box>

            {/* RIGHT: Ask AI */}
            {/* <Box sx={{ flex: "1 1 0", minWidth: 320, maxWidth: 520 }}>
              Ask AI
              <AskAiPanel
                packageId={packageId}
                row={activeRow}
                turns={activeRow ? (turnsByKey[rallyKey(activeRow)] ?? []) : []}
                //onAskAi={handleAskAi}
                onDeleteTurn={handleDeleteTurn}
              />
            </Box> */}
          </Box>

          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <CondensedRallyTimeline
              rallyBadgesRef={rallyBadgesRef}
              rows={rows}
              activeRallyIndex={activeRallyIndex}
              onPickRally={onPickRallyFromCondensed}
              registerRef={registerCondensedRef}
              barsAreaHeightPx={108}
              firefightRallySet={firefightRallySet}
              matchImpactFacts={matchImpactFacts}
            />

            <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
              <RallyColumnsStrip
                rows={rows}
                activeRallyIndex={activeRallyIndex}
                activeShotNumber={activeShotNumber}
                onPickRally={onPickRallyFromDetailed}
                onPickShot={onPickShot}
                registerColumnRef={registerDetailedRef} // new prop (next section)
              />
            </Box>
          </Box>

          {/* <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
            <RallyColumnsStrip
              rows={rows}
              activeRallyIndex={activeRallyIndex}
              activeShotNumber={activeShotNumber}
              onPickRally={onPickRally}
              onPickShot={onPickShot}
            />
          </Box> */}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
